import type {
  CallStatement,
  Declaration,
  DeclaredType,
  ElseIfBranch,
  Expression,
  FunctionDeclaration,
  GlossaProgram,
  LValue,
  ProcedureDeclaration,
  ReturnType,
  Statement,
  Subprogram,
  SwitchCase,
  SwitchCaseValue,
} from '../parser/ast.js';

/**
 * Μετατροπή ΣΥΝΑΡΤΗΣΗ ↔ ΔΙΑΔΙΚΑΣΙΑ σε ολόκληρο πρόγραμμα ΓΛΩΣΣΑΣ — κάθε
 * υποπρόγραμμα γίνεται το αντίθετο είδος του, ΚΑΙ ξαναγράφονται όλα τα
 * σημεία κλήσης του (κύριο πρόγραμμα + κάθε άλλο υποπρόγραμμα) ώστε το
 * αποτέλεσμα να παραμένει έγκυρος, εκτελέσιμος κώδικας.
 *
 * ΣΥΝΑΡΤΗΣΗ → ΔΙΑΔΙΚΑΣΙΑ: προστίθεται μία ΕΠΙΠΛΕΟΝ παράμετρος με το ΙΔΙΟ
 * όνομα με τη συνάρτηση (το σώμα ήδη κάνει "όνομα ← έκφραση", άρα ΔΕΝ
 * χρειάζεται αναδιατύπωση) — κάθε "Ζ ← Χ(...)" γίνεται "ΚΑΛΕΣΕ Χ(..., Ζ)",
 * κάθε "ΓΡΑΨΕ Χ(...)" γίνεται "ΚΑΛΕΣΕ Χ(..., τ)" + "ΓΡΑΨΕ τ" (νέα τοπική
 * μεταβλητή τ). ΠΑΝΤΑ εφικτό — δεν υπάρχει περίπτωση απόρριψης εδώ.
 *
 * ΔΙΑΔΙΚΑΣΙΑ → ΣΥΝΑΡΤΗΣΗ: μόνο όταν το σώμα ΔΕΝ έχει ΔΙΑΒΑΣΕ/ΓΡΑΨΕ/ΚΑΛΕΣΕ
 * (απαγορευμένα σε ΣΥΝΑΡΤΗΣΗ) ΚΑΙ ακριβώς ΜΙΑ παράμετρος ανατίθεται κάπου
 * μέσα στο σώμα (μονοσήμαντη "τιμή εξόδου") ΚΑΙ όλα τα σημεία κλήσης δίνουν
 * σε αυτή τη θέση μια μεταβλητή/στοιχείο πίνακα (όχι σταθερά/έκφραση, αφού
 * χρειάζεται να γίνει στόχος ανάθεσης) — αλλιώς ΔΕΝ μετατρέπεται (παραμένει
 * ΔΙΑΔΙΚΑΣΙΑ, αναφέρεται στο `declined`).
 *
 * "Απορρίπτω αντί να μαντέψω" — ίδια φιλοσοφία με τον `natural-language-parser.ts`.
 */
export interface SubprogramConversionResult {
  readonly program: GlossaProgram;
  /** Ονόματα υποπρογραμμάτων που ΔΕΝ μπόρεσαν να μετατραπούν (παρέμειναν όπως ήταν, αμετάβλητα). */
  readonly declined: readonly string[];
}

const RETURN_TYPE_TO_DECLARED_TYPE: Record<ReturnType, DeclaredType> = {
  ΑΚΕΡΑΙΑ: 'ΑΚΕΡΑΙΕΣ',
  ΠΡΑΓΜΑΤΙΚΗ: 'ΠΡΑΓΜΑΤΙΚΕΣ',
  ΧΑΡΑΚΤΗΡΑΣ: 'ΧΑΡΑΚΤΗΡΕΣ',
  ΛΟΓΙΚΗ: 'ΛΟΓΙΚΕΣ',
};
const DECLARED_TYPE_TO_RETURN_TYPE: Record<DeclaredType, ReturnType> = {
  ΑΚΕΡΑΙΕΣ: 'ΑΚΕΡΑΙΑ',
  ΠΡΑΓΜΑΤΙΚΕΣ: 'ΠΡΑΓΜΑΤΙΚΗ',
  ΧΑΡΑΚΤΗΡΕΣ: 'ΧΑΡΑΚΤΗΡΑΣ',
  ΛΟΓΙΚΕΣ: 'ΛΟΓΙΚΗ',
};

const POS = { line: 1, column: 1 };

// --- Γενικά βοηθητικά για διάσχιση/αναδιατύπωση εκφράσεων/εντολών --------

function isDirectCallTo(expr: Expression, name: string): expr is Expression & { kind: 'FunctionCall' } {
  return expr.kind === 'FunctionCall' && expr.callee.name === name;
}

function expressionReferencesCall(expr: Expression, name: string): boolean {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
    case 'Identifier':
      return false;
    case 'ArrayAccess':
      return expr.indices.some((i) => expressionReferencesCall(i, name));
    case 'FunctionCall':
      return expr.callee.name === name || expr.args.some((a) => expressionReferencesCall(a, name));
    case 'UnaryExpression':
      return expressionReferencesCall(expr.operand, name);
    case 'BinaryExpression':
      return expressionReferencesCall(expr.left, name) || expressionReferencesCall(expr.right, name);
  }
}

function switchCaseValueReferencesCall(v: SwitchCaseValue, name: string): boolean {
  if (v.kind === 'CaseRange') return expressionReferencesCall(v.from, name) || expressionReferencesCall(v.to, name);
  if (v.kind === 'CaseComparison') return expressionReferencesCall(v.value, name);
  return expressionReferencesCall(v, name);
}

function lvalueReferencesCall(lv: LValue, name: string): boolean {
  return lv.kind === 'ArrayAccess' && lv.indices.some((i) => expressionReferencesCall(i, name));
}

// --- Μετονομασία Identifier σε όλο ένα δέντρο εντολών (για ΔΙΑΔΙΚΑΣΙΑ → ΣΥΝΑΡΤΗΣΗ) --

function renameInExpr(expr: Expression, oldName: string, newName: string): Expression {
  switch (expr.kind) {
    case 'NumberLiteral':
    case 'StringLiteral':
    case 'BooleanLiteral':
      return expr;
    case 'Identifier':
      return expr.name === oldName ? { ...expr, name: newName } : expr;
    case 'ArrayAccess':
      return {
        ...expr,
        array: expr.array.name === oldName ? { ...expr.array, name: newName } : expr.array,
        indices: expr.indices.map((i) => renameInExpr(i, oldName, newName)),
      };
    case 'FunctionCall':
      return { ...expr, args: expr.args.map((a) => renameInExpr(a, oldName, newName)) };
    case 'UnaryExpression':
      return { ...expr, operand: renameInExpr(expr.operand, oldName, newName) };
    case 'BinaryExpression':
      return { ...expr, left: renameInExpr(expr.left, oldName, newName), right: renameInExpr(expr.right, oldName, newName) };
  }
}

function renameInLValue(lv: LValue, oldName: string, newName: string): LValue {
  if (lv.kind === 'Identifier') return lv.name === oldName ? { ...lv, name: newName } : lv;
  return { ...lv, array: lv.array.name === oldName ? { ...lv.array, name: newName } : lv.array, indices: lv.indices.map((i) => renameInExpr(i, oldName, newName)) };
}

function renameInSwitchCaseValue(v: SwitchCaseValue, oldName: string, newName: string): SwitchCaseValue {
  if (v.kind === 'CaseRange') return { ...v, from: renameInExpr(v.from, oldName, newName), to: renameInExpr(v.to, oldName, newName) };
  if (v.kind === 'CaseComparison') return { ...v, value: renameInExpr(v.value, oldName, newName) };
  return renameInExpr(v, oldName, newName);
}

function renameInStatements(statements: readonly Statement[], oldName: string, newName: string): Statement[] {
  return statements.map((stmt) => renameInStatement(stmt, oldName, newName));
}

function renameInStatement(stmt: Statement, oldName: string, newName: string): Statement {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return { ...stmt, target: renameInLValue(stmt.target, oldName, newName), value: renameInExpr(stmt.value, oldName, newName) };
    case 'ReadStatement':
      return { ...stmt, targets: stmt.targets.map((t) => renameInLValue(t, oldName, newName)) };
    case 'WriteStatement':
      return { ...stmt, values: stmt.values.map((v) => renameInExpr(v, oldName, newName)) };
    case 'SwapStatement':
      return { ...stmt, left: renameInLValue(stmt.left, oldName, newName), right: renameInLValue(stmt.right, oldName, newName) };
    case 'CallStatement':
      return { ...stmt, args: stmt.args.map((a) => renameInExpr(a, oldName, newName)) };
    case 'IfStatement':
      return {
        ...stmt,
        condition: renameInExpr(stmt.condition, oldName, newName),
        thenBranch: renameInStatements(stmt.thenBranch, oldName, newName),
        elseIfBranches: stmt.elseIfBranches.map(
          (b): ElseIfBranch => ({ condition: renameInExpr(b.condition, oldName, newName), body: renameInStatements(b.body, oldName, newName) }),
        ),
        elseBranch: stmt.elseBranch !== null ? renameInStatements(stmt.elseBranch, oldName, newName) : null,
      };
    case 'ForStatement':
      return {
        ...stmt,
        variable: stmt.variable.name === oldName ? { ...stmt.variable, name: newName } : stmt.variable,
        from: renameInExpr(stmt.from, oldName, newName),
        to: renameInExpr(stmt.to, oldName, newName),
        step: stmt.step !== null ? renameInExpr(stmt.step, oldName, newName) : null,
        body: renameInStatements(stmt.body, oldName, newName),
      };
    case 'WhileStatement':
      return { ...stmt, condition: renameInExpr(stmt.condition, oldName, newName), body: renameInStatements(stmt.body, oldName, newName) };
    case 'RepeatUntilStatement':
      return { ...stmt, body: renameInStatements(stmt.body, oldName, newName), condition: renameInExpr(stmt.condition, oldName, newName) };
    case 'SwitchStatement':
      return {
        ...stmt,
        discriminant: renameInExpr(stmt.discriminant, oldName, newName),
        cases: stmt.cases.map((c): SwitchCase => ({ values: c.values.map((v) => renameInSwitchCaseValue(v, oldName, newName)), body: renameInStatements(c.body, oldName, newName) })),
        elseBody: stmt.elseBody !== null ? renameInStatements(stmt.elseBody, oldName, newName) : null,
      };
  }
}

// --- Έλεγχος "μπορεί να γίνει ΣΥΝΑΡΤΗΣΗ" (καμία ΔΙΑΒΑΣΕ/ΓΡΑΨΕ/ΚΑΛΕΣΕ, ακριβώς 1 ανατιθέμενη παράμετρος) --

function bodyHasIOOrCall(statements: readonly Statement[]): boolean {
  return statements.some((stmt) => {
    switch (stmt.kind) {
      case 'ReadStatement':
      case 'WriteStatement':
      case 'CallStatement':
        return true;
      case 'IfStatement':
        return (
          bodyHasIOOrCall(stmt.thenBranch) ||
          stmt.elseIfBranches.some((b) => bodyHasIOOrCall(b.body)) ||
          (stmt.elseBranch !== null && bodyHasIOOrCall(stmt.elseBranch))
        );
      case 'ForStatement':
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        return bodyHasIOOrCall(stmt.body);
      case 'SwitchStatement':
        return stmt.cases.some((c) => bodyHasIOOrCall(c.body)) || (stmt.elseBody !== null && bodyHasIOOrCall(stmt.elseBody));
      case 'AssignmentStatement':
      case 'SwapStatement':
        return false;
    }
  });
}

function collectAssignedNames(statements: readonly Statement[], candidates: ReadonlySet<string>, into: Set<string>): void {
  for (const stmt of statements) {
    switch (stmt.kind) {
      case 'AssignmentStatement':
        if (stmt.target.kind === 'Identifier' && candidates.has(stmt.target.name)) into.add(stmt.target.name);
        break;
      case 'SwapStatement':
        if (stmt.left.kind === 'Identifier' && candidates.has(stmt.left.name)) into.add(stmt.left.name);
        if (stmt.right.kind === 'Identifier' && candidates.has(stmt.right.name)) into.add(stmt.right.name);
        break;
      case 'IfStatement':
        collectAssignedNames(stmt.thenBranch, candidates, into);
        for (const b of stmt.elseIfBranches) collectAssignedNames(b.body, candidates, into);
        if (stmt.elseBranch !== null) collectAssignedNames(stmt.elseBranch, candidates, into);
        break;
      case 'ForStatement':
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        collectAssignedNames(stmt.body, candidates, into);
        break;
      case 'SwitchStatement':
        for (const c of stmt.cases) collectAssignedNames(c.body, candidates, into);
        if (stmt.elseBody !== null) collectAssignedNames(stmt.elseBody, candidates, into);
        break;
      default:
        break;
    }
  }
}

function findDeclaredType(declarations: readonly Declaration[], name: string): DeclaredType | undefined {
  for (const decl of declarations) {
    if (decl.kind === 'VariableDeclaration' && decl.declarators.some((d) => d.name === name)) return decl.declaredType;
  }
  return undefined;
}

function removeDeclarator(declarations: readonly Declaration[], name: string): Declaration[] {
  return declarations
    .map((decl) => (decl.kind === 'VariableDeclaration' ? { ...decl, declarators: decl.declarators.filter((d) => d.name !== name) } : decl))
    .filter((decl) => decl.kind !== 'VariableDeclaration' || decl.declarators.length > 0);
}

/** Επιστρέφει null όταν η ΔΙΑΔΙΚΑΣΙΑ δεν μπορεί να γίνει ΣΥΝΑΡΤΗΣΗ (βλ. σχόλιο πάνω από `SubprogramConversionResult`). `outputIndex` είναι η θέση της αφαιρούμενης παραμέτρου στην ΑΡΧΙΚΗ λίστα — χρειάζεται για την αναδιατύπωση των σημείων κλήσης (`rewriteCallsForProcedureToFunction`). */
function tryProcedureToFunction(proc: ProcedureDeclaration): { readonly fn: FunctionDeclaration; readonly outputIndex: number } | null {
  if (bodyHasIOOrCall(proc.body)) return null;
  const assigned = new Set<string>();
  collectAssignedNames(proc.body, new Set(proc.parameters), assigned);
  if (assigned.size !== 1) return null;
  const outputParam = [...assigned][0]!;
  const declaredType = findDeclaredType(proc.declarations, outputParam);
  if (declaredType === undefined) return null;

  const fn: FunctionDeclaration = {
    kind: 'FunctionDeclaration',
    name: proc.name,
    parameters: proc.parameters.filter((p) => p !== outputParam),
    returnType: DECLARED_TYPE_TO_RETURN_TYPE[declaredType],
    declarations: removeDeclarator(proc.declarations, outputParam),
    body: renameInStatements(proc.body, outputParam, proc.name),
    // Συνθετικός κόμβος (μόνο εμφάνιση κειμένου στο "Μετατροπή δομών") — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: proc.line,
    column: proc.column,
  };
  return { fn, outputIndex: proc.parameters.indexOf(outputParam) };
}

/**
 * Ψάχνει, σε ΟΛΟ το πρόγραμμα (κύριο σώμα + κάθε υποπρόγραμμα, στην ΑΡΧΙΚΗ
 * — πριν από οποιαδήποτε μετατροπή — μορφή τους), όλα τα σημεία όπου η
 * συνάρτηση `fnName` καλείται ΑΠΕΥΘΕΙΑΣ ως δεξί μέλος μιας ανάθεσης σε ΑΠΛΗ
 * μεταβλητή ("όνομα ← fnName(...)") και μαζεύει το ΣΥΝΟΛΟ των διαφορετικών
 * ονομάτων-στόχων που βρέθηκαν. Χρησιμοποιείται για να διαλέξουμε το όνομα
 * της νέας παραμέτρου όταν η ΣΥΝΑΡΤΗΣΗ γίνεται ΔΙΑΔΙΚΑΣΙΑ — βλ.
 * `functionToProcedure`.
 */
function collectDirectAssignmentTargetNames(bodies: readonly (readonly Statement[])[], fnName: string): Set<string> {
  const names = new Set<string>();
  const visit = (statements: readonly Statement[]): void => {
    for (const stmt of statements) {
      if (stmt.kind === 'AssignmentStatement' && isDirectCallTo(stmt.value, fnName) && stmt.target.kind === 'Identifier') {
        names.add(stmt.target.name);
      }
      switch (stmt.kind) {
        case 'IfStatement':
          visit(stmt.thenBranch);
          for (const b of stmt.elseIfBranches) visit(b.body);
          if (stmt.elseBranch !== null) visit(stmt.elseBranch);
          break;
        case 'ForStatement':
        case 'WhileStatement':
        case 'RepeatUntilStatement':
          visit(stmt.body);
          break;
        case 'SwitchStatement':
          for (const c of stmt.cases) visit(c.body);
          if (stmt.elseBody !== null) visit(stmt.elseBody);
          break;
        default:
          break;
      }
    }
  };
  for (const body of bodies) visit(body);
  return names;
}

/**
 * §"Μετατροπές ΣΥΝΑΡΤΗΣΗΣ ⇄ ΔΙΑΔΙΚΑΣΙΑΣ", Βήμα 1-2: η νέα παράμετρος-εξόδου
 * παίρνει το όνομα που ΗΔΗ χρησιμοποιεί ο μαθητής για το αποτέλεσμα στο
 * σημείο κλήσης (π.χ. "y ← Τετράγωνο(x)" → νέα παράμετρος "y", ΟΧΙ
 * "Τετράγωνο") — αν αυτό υπάρχει με συνέπεια σε ΟΛΑ τα σημεία κλήσης
 * (`preferredName`). Το σώμα ξαναγράφεται ώστε "Τετράγωνο ← ..." να γίνει
 * "y ← ...". Όταν δεν υπάρχει ΕΝΑ σαφές, ασφαλές όνομα (καμία απευθείας
 * ανάθεση κάπου, παραπάνω από ένα διαφορετικά ονόματα, ή σύγκρουση με ήδη
 * υπάρχουσα παράμετρο/δήλωση), πέφτουμε πίσω στο όνομα της ίδιας της
 * συνάρτησης — πάντα ασφαλές, αφού το σώμα ήδη το χρησιμοποιεί.
 */
function functionToProcedure(fn: FunctionDeclaration, preferredName: string | undefined): ProcedureDeclaration {
  const alreadyUsed = new Set<string>(fn.parameters);
  for (const decl of fn.declarations) {
    if (decl.kind === 'ConstantDeclaration') alreadyUsed.add(decl.name);
    else for (const d of decl.declarators) alreadyUsed.add(d.name);
  }
  const paramName = preferredName !== undefined && !alreadyUsed.has(preferredName) ? preferredName : fn.name;

  // Μπαίνει στην ΙΔΙΑ γραμμή δήλωσης με τις υπόλοιπες παραμέτρους του ίδιου
  // τύπου, αν υπάρχει ήδη μία ("ΑΚΕΡΑΙΕΣ: x, y" αντί για ξεχωριστή δεύτερη
  // γραμμή "ΑΚΕΡΑΙΕΣ: y") — πιο κοντά σε αυτό που θα έγραφε ο μαθητής.
  const newParamType = RETURN_TYPE_TO_DECLARED_TYPE[fn.returnType];
  let merged = false;
  const declarations: Declaration[] = fn.declarations.map((decl) => {
    if (!merged && decl.kind === 'VariableDeclaration' && decl.declaredType === newParamType) {
      merged = true;
      return { ...decl, declarators: [...decl.declarators, { name: paramName, dimensions: [] }] };
    }
    return decl;
  });
  if (!merged) {
    declarations.push({ kind: 'VariableDeclaration', declaredType: newParamType, declarators: [{ name: paramName, dimensions: [] }], ...POS });
  }

  return {
    kind: 'ProcedureDeclaration',
    name: fn.name,
    parameters: [...fn.parameters, paramName],
    declarations,
    body: paramName === fn.name ? fn.body : renameInStatements(fn.body, fn.name, paramName),
    // Συνθετικός κόμβος (μόνο εμφάνιση κειμένου στο "Μετατροπή δομών") — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: fn.line,
    column: fn.column,
  };
}

// --- Αναδιατύπωση σημείων κλήσης (ΜΙΑ φορά ανά μετατρεπόμενο όνομα, σε ΟΛΑ τα σώματα) --

interface RewriteScope {
  readonly usedNames: Set<string>;
  readonly extraDeclarations: Declaration[];
}

function freshName(base: string, scope: RewriteScope): string {
  let candidate = base;
  let suffix = 2;
  while (scope.usedNames.has(candidate)) {
    candidate = `${base}${suffix}`;
    suffix++;
  }
  scope.usedNames.add(candidate);
  return candidate;
}

/** ΣΥΝΑΡΤΗΣΗ → ΔΙΑΔΙΚΑΣΙΑ: "Ζ ← fnName(...)" → "ΚΑΛΕΣΕ fnName(..., Ζ)"· "ΓΡΑΨΕ fnName(...)" → "ΚΑΛΕΣΕ fnName(..., τ)" + "ΓΡΑΨΕ τ". null = απόρριψη (ασυνήθιστη, εμφωλευμένη χρήση). */
function rewriteCallsForFunctionToProcedure(statements: readonly Statement[], fnName: string, returnType: ReturnType, scope: RewriteScope): Statement[] | null {
  const result: Statement[] = [];
  for (const stmt of statements) {
    const rewritten = rewriteOneStatementForFunctionToProcedure(stmt, fnName, returnType, scope);
    if (rewritten === null) return null;
    result.push(...rewritten);
  }
  return result;
}

function rewriteOneStatementForFunctionToProcedure(stmt: Statement, fnName: string, returnType: ReturnType, scope: RewriteScope): Statement[] | null {
  switch (stmt.kind) {
    case 'AssignmentStatement': {
      if (isDirectCallTo(stmt.value, fnName)) {
        if (stmt.value.args.some((a) => expressionReferencesCall(a, fnName))) return null;
        if (lvalueReferencesCall(stmt.target, fnName)) return null;
        const call: CallStatement = { kind: 'CallStatement', callee: stmt.value.callee, args: [...stmt.value.args, stmt.target], line: stmt.line, column: stmt.column };
        return [call];
      }
      if (expressionReferencesCall(stmt.value, fnName) || lvalueReferencesCall(stmt.target, fnName)) return null;
      return [stmt];
    }
    case 'WriteStatement': {
      const hoisted: Statement[] = [];
      const newValues: Expression[] = [];
      let changed = false;
      for (const v of stmt.values) {
        if (isDirectCallTo(v, fnName)) {
          if (v.args.some((a) => expressionReferencesCall(a, fnName))) return null;
          const temp = freshName(fnName, scope);
          scope.extraDeclarations.push({ kind: 'VariableDeclaration', declaredType: RETURN_TYPE_TO_DECLARED_TYPE[returnType], declarators: [{ name: temp, dimensions: [] }], ...POS });
          const tempIdentifier: Expression = { kind: 'Identifier', name: temp, line: v.line, column: v.column };
          hoisted.push({ kind: 'CallStatement', callee: v.callee, args: [...v.args, tempIdentifier], line: v.line, column: v.column });
          newValues.push(tempIdentifier);
          changed = true;
        } else if (expressionReferencesCall(v, fnName)) {
          return null;
        } else {
          newValues.push(v);
        }
      }
      return changed ? [...hoisted, { ...stmt, values: newValues }] : [stmt];
    }
    case 'ReadStatement':
      return stmt.targets.some((t) => lvalueReferencesCall(t, fnName)) ? null : [stmt];
    case 'SwapStatement':
      return lvalueReferencesCall(stmt.left, fnName) || lvalueReferencesCall(stmt.right, fnName) ? null : [stmt];
    case 'CallStatement':
      return stmt.args.some((a) => expressionReferencesCall(a, fnName)) ? null : [stmt];
    case 'IfStatement': {
      if (expressionReferencesCall(stmt.condition, fnName)) return null;
      const thenBranch = rewriteCallsForFunctionToProcedure(stmt.thenBranch, fnName, returnType, scope);
      if (thenBranch === null) return null;
      const elseIfBranches: ElseIfBranch[] = [];
      for (const b of stmt.elseIfBranches) {
        if (expressionReferencesCall(b.condition, fnName)) return null;
        const body = rewriteCallsForFunctionToProcedure(b.body, fnName, returnType, scope);
        if (body === null) return null;
        elseIfBranches.push({ condition: b.condition, body });
      }
      let elseBranch: readonly Statement[] | null = null;
      if (stmt.elseBranch !== null) {
        elseBranch = rewriteCallsForFunctionToProcedure(stmt.elseBranch, fnName, returnType, scope);
        if (elseBranch === null) return null;
      }
      return [{ ...stmt, thenBranch, elseIfBranches, elseBranch }];
    }
    case 'ForStatement': {
      if (
        expressionReferencesCall(stmt.from, fnName) ||
        expressionReferencesCall(stmt.to, fnName) ||
        (stmt.step !== null && expressionReferencesCall(stmt.step, fnName))
      ) {
        return null;
      }
      const body = rewriteCallsForFunctionToProcedure(stmt.body, fnName, returnType, scope);
      return body === null ? null : [{ ...stmt, body }];
    }
    case 'WhileStatement': {
      if (expressionReferencesCall(stmt.condition, fnName)) return null;
      const body = rewriteCallsForFunctionToProcedure(stmt.body, fnName, returnType, scope);
      return body === null ? null : [{ ...stmt, body }];
    }
    case 'RepeatUntilStatement': {
      const body = rewriteCallsForFunctionToProcedure(stmt.body, fnName, returnType, scope);
      if (body === null) return null;
      if (expressionReferencesCall(stmt.condition, fnName)) return null;
      return [{ ...stmt, body }];
    }
    case 'SwitchStatement': {
      if (expressionReferencesCall(stmt.discriminant, fnName)) return null;
      const cases: SwitchCase[] = [];
      for (const c of stmt.cases) {
        if (c.values.some((v) => switchCaseValueReferencesCall(v, fnName))) return null;
        const body = rewriteCallsForFunctionToProcedure(c.body, fnName, returnType, scope);
        if (body === null) return null;
        cases.push({ values: c.values, body });
      }
      let elseBody: readonly Statement[] | null = null;
      if (stmt.elseBody !== null) {
        elseBody = rewriteCallsForFunctionToProcedure(stmt.elseBody, fnName, returnType, scope);
        if (elseBody === null) return null;
      }
      return [{ ...stmt, cases, elseBody }];
    }
  }
}

/** ΔΙΑΔΙΚΑΣΙΑ → ΣΥΝΑΡΤΗΣΗ: "ΚΑΛΕΣΕ procName(..., στόχος)" → "στόχος ← procName(...)". null = απόρριψη (η θέση-εξόδου δεν είναι lvalue σε κάποιο σημείο κλήσης). */
function rewriteCallsForProcedureToFunction(statements: readonly Statement[], procName: string, outputIndex: number): Statement[] | null {
  const result: Statement[] = [];
  for (const stmt of statements) {
    const rewritten = rewriteOneStatementForProcedureToFunction(stmt, procName, outputIndex);
    if (rewritten === null) return null;
    result.push(rewritten);
  }
  return result;
}

function rewriteOneStatementForProcedureToFunction(stmt: Statement, procName: string, outputIndex: number): Statement | null {
  switch (stmt.kind) {
    case 'CallStatement': {
      if (stmt.callee.name !== procName) return stmt;
      const target = stmt.args[outputIndex];
      if (target === undefined || (target.kind !== 'Identifier' && target.kind !== 'ArrayAccess')) return null;
      const remainingArgs = stmt.args.filter((_, i) => i !== outputIndex);
      const call: Expression = { kind: 'FunctionCall', callee: stmt.callee, args: remainingArgs, line: stmt.line, column: stmt.column };
      return { kind: 'AssignmentStatement', target, value: call, line: stmt.line, column: stmt.column };
    }
    case 'IfStatement': {
      const thenBranch = rewriteCallsForProcedureToFunction(stmt.thenBranch, procName, outputIndex);
      if (thenBranch === null) return null;
      const elseIfBranches: ElseIfBranch[] = [];
      for (const b of stmt.elseIfBranches) {
        const body = rewriteCallsForProcedureToFunction(b.body, procName, outputIndex);
        if (body === null) return null;
        elseIfBranches.push({ condition: b.condition, body });
      }
      let elseBranch: readonly Statement[] | null = null;
      if (stmt.elseBranch !== null) {
        elseBranch = rewriteCallsForProcedureToFunction(stmt.elseBranch, procName, outputIndex);
        if (elseBranch === null) return null;
      }
      return { ...stmt, thenBranch, elseIfBranches, elseBranch };
    }
    case 'ForStatement': {
      const body = rewriteCallsForProcedureToFunction(stmt.body, procName, outputIndex);
      return body === null ? null : { ...stmt, body };
    }
    case 'WhileStatement': {
      const body = rewriteCallsForProcedureToFunction(stmt.body, procName, outputIndex);
      return body === null ? null : { ...stmt, body };
    }
    case 'RepeatUntilStatement': {
      const body = rewriteCallsForProcedureToFunction(stmt.body, procName, outputIndex);
      return body === null ? null : { ...stmt, body };
    }
    case 'SwitchStatement': {
      const cases: SwitchCase[] = [];
      for (const c of stmt.cases) {
        const body = rewriteCallsForProcedureToFunction(c.body, procName, outputIndex);
        if (body === null) return null;
        cases.push({ values: c.values, body });
      }
      let elseBody: readonly Statement[] | null = null;
      if (stmt.elseBody !== null) {
        elseBody = rewriteCallsForProcedureToFunction(stmt.elseBody, procName, outputIndex);
        if (elseBody === null) return null;
      }
      return { ...stmt, cases, elseBody };
    }
    default:
      return stmt;
  }
}

// --- Ονόματα ήδη σε χρήση σε ένα σώμα (για ασφαλή δημιουργία νέων ονομάτων) --

function collectAllNames(declarations: readonly Declaration[], parameters: readonly string[], body: readonly Statement[]): Set<string> {
  const names = new Set<string>(parameters);
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') names.add(decl.name);
    else for (const d of decl.declarators) names.add(d.name);
  }
  const visitExpr = (expr: Expression): void => {
    if (expr.kind === 'Identifier') names.add(expr.name);
    else if (expr.kind === 'ArrayAccess') {
      names.add(expr.array.name);
      expr.indices.forEach(visitExpr);
    } else if (expr.kind === 'FunctionCall') expr.args.forEach(visitExpr);
    else if (expr.kind === 'UnaryExpression') visitExpr(expr.operand);
    else if (expr.kind === 'BinaryExpression') {
      visitExpr(expr.left);
      visitExpr(expr.right);
    }
  };
  const visitLValue = (lv: LValue): void => {
    names.add(lv.kind === 'Identifier' ? lv.name : lv.array.name);
    if (lv.kind === 'ArrayAccess') lv.indices.forEach(visitExpr);
  };
  const visitStatements = (statements: readonly Statement[]): void => {
    for (const stmt of statements) {
      switch (stmt.kind) {
        case 'AssignmentStatement':
          visitLValue(stmt.target);
          visitExpr(stmt.value);
          break;
        case 'ReadStatement':
          stmt.targets.forEach(visitLValue);
          break;
        case 'WriteStatement':
          stmt.values.forEach(visitExpr);
          break;
        case 'SwapStatement':
          visitLValue(stmt.left);
          visitLValue(stmt.right);
          break;
        case 'CallStatement':
          names.add(stmt.callee.name);
          stmt.args.forEach(visitExpr);
          break;
        case 'IfStatement':
          visitExpr(stmt.condition);
          visitStatements(stmt.thenBranch);
          for (const b of stmt.elseIfBranches) {
            visitExpr(b.condition);
            visitStatements(b.body);
          }
          if (stmt.elseBranch !== null) visitStatements(stmt.elseBranch);
          break;
        case 'ForStatement':
          names.add(stmt.variable.name);
          visitExpr(stmt.from);
          visitExpr(stmt.to);
          if (stmt.step !== null) visitExpr(stmt.step);
          visitStatements(stmt.body);
          break;
        case 'WhileStatement':
          visitExpr(stmt.condition);
          visitStatements(stmt.body);
          break;
        case 'RepeatUntilStatement':
          visitStatements(stmt.body);
          visitExpr(stmt.condition);
          break;
        case 'SwitchStatement':
          visitExpr(stmt.discriminant);
          for (const c of stmt.cases) {
            for (const v of c.values) {
              if (v.kind === 'CaseRange') {
                visitExpr(v.from);
                visitExpr(v.to);
              } else if (v.kind === 'CaseComparison') visitExpr(v.value);
              else visitExpr(v);
            }
            visitStatements(c.body);
          }
          if (stmt.elseBody !== null) visitStatements(stmt.elseBody);
          break;
      }
    }
  };
  visitStatements(body);
  return names;
}

// --- Δημόσια αρχή εισόδου -------------------------------------------------

/** Μετατρέπει (best-effort) ΚΑΘΕ υποπρόγραμμα ενός προγράμματος ΓΛΩΣΣΑΣ στο αντίθετο είδος του — βλ. σχόλιο στην κορυφή του αρχείου. */
export function convertAllSubprograms(program: GlossaProgram): SubprogramConversionResult {
  if (program.subprograms.length === 0) return { program, declined: [] };

  // Βήμα 1: υπολόγισε τη νέα ΚΕΦΑΛΙΔΑ κάθε υποπρογράμματος (χωρίς ακόμα αναδιατύπωση σημείων κλήσης).
  const converted = new Map<string, { readonly newDecl: Subprogram; readonly direction: 'toProcedure' | 'toFunction'; readonly returnType?: ReturnType; readonly outputIndex?: number }>();
  const declined: string[] = [];
  const originalBodies: (readonly Statement[])[] = [program.body, ...program.subprograms.map((s) => s.body)];

  for (const sub of program.subprograms) {
    if (sub.kind === 'FunctionDeclaration') {
      const targetNames = collectDirectAssignmentTargetNames(originalBodies, sub.name);
      const preferredName = targetNames.size === 1 ? [...targetNames][0] : undefined;
      converted.set(sub.name, { newDecl: functionToProcedure(sub, preferredName), direction: 'toProcedure', returnType: sub.returnType });
    } else {
      const result = tryProcedureToFunction(sub);
      if (result === null) {
        declined.push(sub.name);
        continue;
      }
      converted.set(sub.name, { newDecl: result.fn, direction: 'toFunction', outputIndex: result.outputIndex });
    }
  }

  // Βήμα 2: "στεγνή δοκιμή" — έλεγξε ότι ΟΛΑ τα σημεία κλήσης κάθε μετατρεπόμενου ονόματος είναι συμβατά, σε ΟΛΑ τα σώματα (κύριο + κάθε υποπρόγραμμα, με τη ΝΕΑ κεφαλίδα όπου ισχύει).
  const allBodiesForScan: (readonly Statement[])[] = [program.body, ...program.subprograms.map((s) => (converted.get(s.name)?.newDecl ?? s).body)];
  for (const [name, info] of converted) {
    let ok = true;
    for (const body of allBodiesForScan) {
      if (info.direction === 'toProcedure') {
        const trialScope: RewriteScope = { usedNames: new Set(), extraDeclarations: [] };
        if (rewriteCallsForFunctionToProcedure(body, name, info.returnType!, trialScope) === null) {
          ok = false;
          break;
        }
      } else if (rewriteCallsForProcedureToFunction(body, name, info.outputIndex!) === null) {
        ok = false;
        break;
      }
    }
    if (!ok) {
      converted.delete(name);
      declined.push(name);
    }
  }

  if (converted.size === 0) {
    return { program, declined };
  }

  // Βήμα 3: εφάρμοσε πραγματικά την αναδιατύπωση σε κάθε σώμα (κύριο πρόγραμμα + κάθε υποπρόγραμμα), μία φορά ανά μετατρεπόμενο όνομα.
  const rewriteBody = (declarations: readonly Declaration[], parameters: readonly string[], body: readonly Statement[]): { declarations: Declaration[]; body: Statement[] } => {
    const scope: RewriteScope = { usedNames: collectAllNames(declarations, parameters, body), extraDeclarations: [] };
    let currentBody: readonly Statement[] = body;
    for (const [name, info] of converted) {
      if (info.direction === 'toProcedure') {
        currentBody = rewriteCallsForFunctionToProcedure(currentBody, name, info.returnType!, scope)!;
      } else {
        currentBody = rewriteCallsForProcedureToFunction(currentBody, name, info.outputIndex!)!;
      }
    }
    return { declarations: [...declarations, ...scope.extraDeclarations], body: [...currentBody] };
  };

  const mainRewrite = rewriteBody(program.declarations, [], program.body);

  const newSubprograms: Subprogram[] = program.subprograms.map((sub) => {
    const info = converted.get(sub.name);
    const base = info?.newDecl ?? sub;
    const rewritten = rewriteBody(base.declarations, base.parameters, base.body);
    return { ...base, declarations: rewritten.declarations, body: rewritten.body } as Subprogram;
  });

  return {
    program: { ...program, declarations: mainRewrite.declarations, body: mainRewrite.body, subprograms: newSubprograms },
    declined,
  };
}
